import { toast } from '@tempo-io/sui-sonner';
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

type SnackbarState = {
  message: string;
  severity?: 'error' | 'warning' | 'info' | 'success';
};

type SnackbarContextType = {
  state?: SnackbarState;
  setMessage: Dispatch<SetStateAction<SnackbarState | undefined>>;
  onGlobalError: (error: unknown, variables: unknown) => void;
};

export const SnackbarContext = createContext<SnackbarContextType>({
  setMessage: () => {
    return;
  },
  onGlobalError: () => {
    return;
  },
});

type SnackbarProviderProps = {
  children: ReactNode;
};

export const useSetSnackbarGlobalError = () => useContext(SnackbarContext).onGlobalError;
export const useSetSnackbarMessage = () => useContext(SnackbarContext).setMessage;

export const SnackbarProvider = ({ children }: SnackbarProviderProps) => {
  const [state, setState] = useState<SnackbarState>();
  const { t } = useTranslation('translation');

  useEffect(() => {
    if (!state) return;
    const { message, severity = 'info' } = state;
    if (severity === 'error') toast.error(message, { duration: 6000 });
    else if (severity === 'warning') toast.warning(message, { duration: 6000 });
    else if (severity === 'success') toast.success(message, { duration: 6000 });
    else toast(message, { duration: 6000 });
  }, [state]);

  const handleGlobalError = (error: unknown) => {
    const responseData: Record<string, unknown> =
      (error as { response?: { data?: Record<string, unknown> } })?.response?.data ?? {};
    const message = (responseData.message as string) || '';

    // to do && t(message, '') !== '') {
    // We have a specific translation for this server error message
    // We can use {{template}} substitution in the translation string for any of the response data properties.
    let key: string;
    //We choose the matching key. By default we use 'generic'
    //The most common pattern is ideaManagement.serverErrors.<error_message>
    if (t(`ideaManagement.serverErrors.${message}`, '')) {
      key = `ideaManagement.serverErrors.${message}`;
    } else if (t(message, '')) {
      key = message;
    } else {
      key = 'ideaManagement.serverErrors.generic';
    }
    setState({
      message: t(key, message, responseData),
      severity: 'error',
    });
  };

  const context = {
    state,
    setMessage: setState,
    onGlobalError: handleGlobalError,
  };

  return <SnackbarContext.Provider value={context}>{children}</SnackbarContext.Provider>;
};
